import { useState } from "react";
import { History, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCreditLotEntries } from "@/hooks/queries/useCreditLotEntries";

const ENTRY_TYPE_LABELS: Record<string, string> = {
  spend: "Dépense",
  refund: "Remboursement",
  convert_to_epargne: "Converti en épargne",
};

interface CreditLotEntriesDialogProps {
  lotId: number;
}

export function CreditLotEntriesDialog({ lotId }: CreditLotEntriesDialogProps) {
  const [open, setOpen] = useState(false);
  // Only fetched while the dialog is actually open.
  const { data: entries = [], isLoading } = useCreditLotEntries(open ? lotId : null);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button size="sm" variant="ghost">
          <History size={13} /> Historique
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Historique du lot</DialogTitle>
          <DialogDescription>Mouvements enregistrés sur ce lot de crédit.</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <Loader2 size={16} className="animate-spin text-subtle mx-auto" />
        ) : entries.length === 0 ? (
          <p className="text-[12px] text-muted-foreground py-2">Aucun mouvement enregistré.</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {entries.map((entry) => (
              <div key={entry.id} className="border border-border rounded-lg px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[12px] font-medium text-body">
                    {ENTRY_TYPE_LABELS[entry.entryType] ?? entry.entryType}
                  </span>
                  <span className="shrink-0 text-[12px] font-semibold text-foreground">
                    {parseFloat(entry.amount).toLocaleString("fr-FR")} XAF
                  </span>
                </div>
                <p className="text-[10.5px] text-muted-foreground">
                  {new Date(entry.createdAt).toLocaleString("fr-FR")}
                  {entry.refType && ` · ${entry.refType}${entry.refId ? ` #${entry.refId}` : ""}`}
                </p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
